import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg, fmtDate, openFile } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, NativeSelect, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { sectionsOf } from "@/components/registration";

const STATUS_OPTS = ["submitted", "email_verification_pending", "under_review", "additional_info_required", "approved", "rejected", "suspended"]
  .map((s) => ({ value: s, label: s.replace(/_/g, " ") }));
const NEXT = {
  submitted: ["under_review", "rejected"], under_review: ["additional_info_required", "approved", "rejected"],
  additional_info_required: ["under_review", "rejected"], approved: ["suspended"], suspended: ["approved"], email_verification_pending: ["rejected"],
};

export function ApplicationsList() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [cls, setCls] = useState("");
  useEffect(() => {
    api.get("/admin/registration/applications", { params: { status, classification: cls } }).then((r) => setItems(r.data));
  }, [status, cls]);
  return (
    <div>
      <PageHeader eyebrow="Customer Service" title="Registration review" description="Corporate and government applications are never auto-approved." />
      <div className="flex gap-3 mb-4 max-w-lg">
        <NativeSelect testId="filter-app-status" value={status} onChange={setStatus} options={STATUS_OPTS} placeholder="All statuses" />
        <NativeSelect testId="filter-app-class" value={cls} onChange={setCls} placeholder="All types"
          options={["residential", "corporate", "government"].map((c) => ({ value: c, label: c }))} />
      </div>
      {items.length === 0 ? <Empty>No applications.</Empty> : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left p-3">Reference</th><th className="text-left">Type</th><th className="text-left">Applicant</th><th className="text-left">Updated</th><th className="text-right p-3">Status</th></tr></thead>
            <tbody>{items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-amber-50/40">
                <td className="p-3"><Link className="font-mono text-amber-800 underline" to={`/app/applications/${a.id}`} data-testid={`review-app-${a.reference_no}`}>{a.reference_no}</Link></td>
                <td className="capitalize">{a.classification}</td><td>{a.data.organization_name || a.data.entity_name || `${a.data.first_name || ""} ${a.data.last_name || ""}`}<p className="text-xs text-slate-500">{a.user_email}</p></td>
                <td className="text-xs">{fmtDate(a.updated_at)}</td><td className="p-3 text-right"><StatusBadge status={a.status} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ApplicationReview() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get(`/registration/applications/${id}`).then((r) => setApp(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!app) return null;
  const schema = app.schema;
  const verify = async (fid, status) => {
    try { await api.post(`/admin/registration/documents/${fid}/verify`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const decide = async () => {
    try { await api.post(`/admin/registration/applications/${id}/status`, { status: action, reason }); toast.success("Status updated"); setAction(null); setReason(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow={app.reference_no} title={`${schema.label} application`} description={`Applicant login: ${app.user_email}`}
        actions={<><StatusBadge status={app.status} testId="review-status" />{(NEXT[app.status] || []).map((s) => (
          <Button key={s} size="sm" variant={s === "approved" ? "default" : "outline"} onClick={() => setAction(s)} data-testid={`review-action-${s}`} className="capitalize">{s === "approved" ? "Approve" : s === "suspended" ? "Suspend" : s === "rejected" ? "Reject" : s.replace(/_/g, " ")}</Button>
        ))}</>} />
      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Application data" className="lg:col-span-2">
          {sectionsOf(schema).map((s) => (
            <div key={s} className="mb-5">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">{s}</p>
              <dl className="grid sm:grid-cols-2 gap-x-6 text-sm">{schema.fields.filter((f) => f.section === s).map((f) => (
                <div key={f.key} className="flex justify-between gap-3 border-b border-slate-100 py-1.5"><dt className="text-slate-500">{f.label}</dt>
                  <dd className="font-medium text-right">{app.data[f.key] === true ? "Yes" : (f.type === "select" && f.options.find((o) => o.value === app.data[f.key])?.label) || app.data[f.key] || "—"}</dd></div>
              ))}</dl>
            </div>
          ))}
        </Panel>
        <div className="space-y-6">
          <Panel title="Supporting documents" testId="review-documents">
            {app.documents.length === 0 && <p className="text-sm text-slate-500">None uploaded.</p>}
            {app.documents.map((d) => (
              <div key={d.id} className="py-2 border-b border-slate-100 last:border-0">
                <button className="text-sm text-amber-800 underline text-left" onClick={() => openFile(d.id)}>{schema.documents.find((x) => x.key === d.doc_type)?.label || d.doc_type}</button>
                <div className="flex items-center gap-2 mt-1"><StatusBadge status={d.verification_status} testId={`doc-status-${d.doc_type}`} />
                  <Button size="sm" variant="outline" className="h-7" onClick={() => verify(d.id, "verified")} data-testid={`verify-doc-${d.doc_type}`}>Verify</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-rose-700" onClick={() => verify(d.id, "rejected")} data-testid={`reject-doc-${d.doc_type}`}>Reject</Button></div>
              </div>
            ))}
          </Panel>
          <Panel title="Audit trail" testId="review-history">
            <ol className="space-y-3 text-sm">{app.history.map((h, i) => (
              <li key={i} className="border-l-2 border-amber-400 pl-3"><StatusBadge status={h.to} /><p className="text-xs text-slate-500 mt-1">{fmtDate(h.at)} · {h.by_email}</p>{h.reason && <p className="text-xs">{h.reason}</p>}</li>
            ))}</ol>
          </Panel>
        </div>
      </div>
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent data-testid="review-decision-dialog">
          <DialogHeader><DialogTitle className="capitalize">Mark as {action?.replace(/_/g, " ")}</DialogTitle>
            <DialogDescription>The reason is saved in the audit trail and the applicant is notified.</DialogDescription></DialogHeader>
          {action === "approved" && <p className="text-sm text-slate-500">Approval activates the subscriber registration and links the representative/account. New-service requests go to the separate inspection workflow.</p>}
          <Textarea placeholder="Reason / notes (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="review-reason-input" />
          <DialogFooter><Button disabled={reason.length < 3} onClick={decide} data-testid="review-decision-submit">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
